import React, { useEffect, useState, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Redirect, Route, Switch } from 'react-router';
import { connect, useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { GeneralRouterTabs, LoadingIndicator, ErrorBoundary } from 'sharedComponents';
import { getTabOptions } from './helpers';
import { PowertrainConfiguration } from './PowertrainConfiguration';
import { PowertrainConfiguration as PowertrainConfigurationV2 } from './PowertrainConfigurationV2';
import { SubscriptionStatus } from './SubscriptionStatus';
import { SensorFirmwareManagement } from './SensorFirmwareManagement';
import { UserManagement } from './UserManagement';
import { getModel } from './selectors';
import * as actions from './SubscriptionStatus/actions';
import { AssetManagement } from './AssetManagement';
import UserCapabilityHelper from '../../helpers/userCapabilityHelper';
import { triggerReportIssueDialog } from '../PowertrainOverview/actions';
import { NON_POWERTRAIN_ORGANIZATION_TAB_OPTIONS } from './constants';

const GatewayManagement = React.lazy(() => import('./GatewayManagement'));

const PowertrainToolsComponent = (props) => {
    const { t: translate } = useTranslation();
    const location = useLocation();
    const dispatch = useDispatch();
    const generalFilterAssetIds = props?.model?.generalFilter;
    const capabilities = useSelector(state => state?.overview?.userCapabilities);
    const selectedOrganization = props?.model?.selectedOrganization;

    const [tabOptions, setTabOptions] = useState({});
    const [defaultRoute, setDefaultRoute] = useState(null);

    const handleReportIssue = () => {
        dispatch(triggerReportIssueDialog());
    };

    const routeClassName = location.pathname
        .replace(/^\//, '')
        .replace(/\//g, '-')
        || '';

    useEffect(() => {
        const userCapabilities = new UserCapabilityHelper(capabilities);
        const options = getTabOptions(translate);
        const hasGatewayAdminViewCapability = userCapabilities?.hasGatewayAdminViewCapability();
        const hasGatewayAdminReadViewCapability = userCapabilities?.hasGatewayAdminReadView();
        const hasGatewayMaintenanceModeCapability = userCapabilities?.hasGatewayMaintenanceModeCapability();
        const isNonPowertrainOrganization = selectedOrganization?.isPowertrainOrganization === false;

        if (!hasGatewayAdminViewCapability && !hasGatewayAdminReadViewCapability) {
            delete options.gatewayManagement;
        }
        if (!userCapabilities?.hasCiamViewCapability()) {
            delete options.userManagement;
            delete options.assetManagemnt;
        }
        if (!hasGatewayMaintenanceModeCapability) {
            delete options.sensorFirmwareManagement;
        }
        if (!userCapabilities?.hasPowertrainConfiguratorOldCapability()) {
            delete options.powertrainConfiguration;
        }
        if (!userCapabilities?.hasPowertrainConfiguratorCapability()) {
            delete options.powertrainConfigurationV2;
        }

        if (isNonPowertrainOrganization) {
            // Non-powertrain organizations only get access to user and asset management
            Object.keys(options).forEach((key) => {
                if (!NON_POWERTRAIN_ORGANIZATION_TAB_OPTIONS.includes(key)) {
                    delete options[key];
                }
            });
        }

        const firstTab = Object.values(options).find(tab => tab?.route);
        setDefaultRoute(firstTab?.route || tabOptions.powertrainConfiguration.route);

        setTabOptions(options);
    }, [capabilities, translate, selectedOrganization]);

    useEffect(() => {
        if (generalFilterAssetIds?.length > 0) {
            const assetIds = generalFilterAssetIds.map((item) => item?.id);
            const assetIdParams = { assetIds, type: null, statusCodes: [], maxEndDate: null };
            props.actions.getSubscriptionInfo(assetIdParams);
        }
    }, [generalFilterAssetIds, props.actions]);

    if (!defaultRoute) {
        return <LoadingIndicator />;
    }

    return (
        <div className={`general-page-content ${routeClassName}`}>
            <GeneralRouterTabs
                options={tabOptions}
                variant='primary'
            >
                <Suspense fallback={<LoadingIndicator />}>
                    <Switch>
                        {tabOptions?.powertrainConfiguration?.route &&
                            <Route path={tabOptions.powertrainConfiguration.route}>
                                <PowertrainConfiguration />
                            </Route>
                        }
                        {tabOptions?.powertrainConfigurationV2?.route &&
                            <Route path={tabOptions.powertrainConfigurationV2.route}>
                                <PowertrainConfigurationV2 />
                            </Route>
                        }
                        {tabOptions?.subscriptionStatus?.route &&
                            <Route path={tabOptions.subscriptionStatus.route}>
                                <ErrorBoundary key='error-boundary-subscription-status' onReportIssue={handleReportIssue}>
                                    <SubscriptionStatus />
                                </ErrorBoundary>
                            </Route>
                        }
                        {tabOptions?.userManagement?.route &&
                            <Route path={tabOptions.userManagement.route}>
                                <UserManagement />
                            </Route>
                        }
                        {tabOptions?.assetManagemnt?.route &&
                            <Route path={tabOptions.assetManagemnt.route}>
                                <AssetManagement />
                            </Route>
                        }
                        {tabOptions?.gatewayManagement?.route &&
                            <Route path={tabOptions.gatewayManagement.route}>
                                <ErrorBoundary key='error-boundary-gateway-management' onReportIssue={handleReportIssue}>
                                    <GatewayManagement />
                                </ErrorBoundary>
                            </Route>
                        }
                        {tabOptions?.sensorFirmwareManagement?.route &&
                            <Route path={tabOptions.sensorFirmwareManagement.route}>
                                <SensorFirmwareManagement />
                            </Route>
                        }
                        <Redirect to={defaultRoute} />
                    </Switch>
                </Suspense>
            </GeneralRouterTabs>
        </div>
    );
};

const mapStateToProps = (state) => {
    const model = getModel(state);
    return {
        model
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        actions: bindActionCreators(actions, dispatch)
    };
};

const PowertrainToolsConn = connect(
    mapStateToProps,
    mapDispatchToProps
)(PowertrainToolsComponent);

PowertrainToolsComponent.propTypes = {
    model: PropTypes.shape({
        generalFilter: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        })),
        selectedOrganization: PropTypes.shape({
            organizationID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            isPowertrainOrganization: PropTypes.bool
        })
    }).isRequired,
    actions: PropTypes.shape({
        getSubscriptionInfo: PropTypes.func.isRequired
    }).isRequired
};

export default PowertrainToolsConn;
